"use client";

// React Query hooks for the Community Showcase.
//   - Public reads (feed / list / detail) hit GET /api/public/showcase[/:id].
//   - Mutations (publish / unpublish / like) hit POST /api/actions/showcase-*.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";

export type ShowcaseCategory = "create" | "design" | "optimize" | "code";

export type ShowcaseItem = {
  id: string;
  user_id: string;
  source_asset_id: string | null;
  title: string;
  description: string;
  source_tool: string | null;
  category: ShowcaseCategory | null;
  image_bucket: string;
  image_path: string;
  image_url: string;
  image_width: number | null;
  image_height: number | null;
  extension: string;
  kind: "image" | "model";
  creator_handle: string;
  creator_avatar_url: string | null;
  status: "published" | "hidden" | "removed";
  is_featured: boolean;
  like_count: number;
  view_count: number;
  created_at: string;
  updated_at: string;
};

export type ShowcaseListPage = { items: ShowcaseItem[]; nextOffset: number | null };

async function callAction<T>(action: string, body: Record<string, unknown> = {}): Promise<T> {
  return apiFetch<T>(`/api/actions/${action}`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "content-type": "application/json" },
  });
}

// ---------------------------------------------------------------------------
// Public reads
// ---------------------------------------------------------------------------

/** Featured-first feed for the home-page carousel. */
export function useShowcaseFeed(limit = 12, enabled = true) {
  return useQuery({
    queryKey: ["showcase", "feed", limit],
    enabled,
    queryFn: () =>
      apiFetch<ShowcaseListPage>(`/api/public/showcase?featured=1&limit=${limit}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export type ShowcaseListFilters = {
  sort?: "newest" | "liked";
  tool?: string | null;
  pageSize?: number;
  enabled?: boolean;
};

/** Infinite gallery grid with offset pagination. */
export function useShowcaseList(filters: ShowcaseListFilters = {}) {
  const { sort = "newest", tool = null, pageSize = 24, enabled = true } = filters;
  return useInfiniteQuery({
    queryKey: ["showcase", "list", { sort, tool, pageSize }],
    enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ sort, limit: String(pageSize), offset: String(pageParam) });
      if (tool) params.set("tool", tool);
      return apiFetch<ShowcaseListPage>(`/api/public/showcase?${params.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    refetchOnWindowFocus: false,
  });
}

export type ShowcaseItemResponse = { item: ShowcaseItem; viewerHasLiked: boolean };

/** Record a view at most once per session per item (sessionStorage-guarded). */
export function recordShowcaseView(id: string): void {
  if (typeof window === "undefined") return;
  const key = `sc_viewed_${id}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    // Private mode / storage disabled — fall through and still record once.
  }
  void apiFetch(`/api/public/showcase/${id}`, { method: "POST" }).catch(() => undefined);
}

export function useShowcaseItem(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ["showcase", "item", id],
    enabled: Boolean(id) && enabled,
    queryFn: () => apiFetch<ShowcaseItemResponse>(`/api/public/showcase/${id}`),
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export type PublishInput = {
  assetId: string;
  title: string;
  description?: string;
  sourceTool?: string | null;
  modelSnapshot?: string | null;
};

export function usePublishToShowcase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PublishInput) => callAction<ShowcaseItem>("showcase-publish", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["showcase"] });
    },
  });
}

/** Publish a captured view (base64 data URL) with no backing asset. */
export type PublishImageInput = {
  image: string;
  title: string;
  description?: string;
  sourceTool?: string | null;
};

export function usePublishImageToShowcase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PublishImageInput) => callAction<ShowcaseItem>("showcase-publish-image", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["showcase"] });
    },
  });
}

export function useUnpublishShowcaseItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) => callAction<{ deleted: true }>("showcase-unpublish", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["showcase"] });
    },
  });
}

export function useSetShowcaseLike() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; liked: boolean }) =>
      callAction<{ liked: boolean; likeCount: number }>("showcase-like", input),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["showcase", "item", variables.id] });
      qc.invalidateQueries({ queryKey: ["showcase", "list"] });
    },
  });
}

export function useMyShowcaseItems(enabled = true) {
  return useQuery({
    queryKey: ["showcase", "mine"],
    enabled,
    queryFn: () => callAction<ShowcaseItem[]>("showcase-mine"),
    refetchOnWindowFocus: false,
  });
}
